import { useEffect, useRef, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import * as customersApi from '@/api/customers'
import * as templatesApi from '@/api/templates'
import { useAuth } from '@/auth/AuthContext'
import { CustomerLogo } from '@/components/CustomerLogo'
import { SettingsShell } from '@/components/SettingsShell'

function emptyForm() {
  return {
    name: '',
    notes: '',
    company_owner_representative: '',
    it_manager_system_administrator: '',
    template: 'it',
  }
}

/** Label row: human name on the left, optional {{merge}} token on the right. */
function FieldLabel({ htmlFor, children, mergeToken }) {
  return (
    <div className="mb-0.5 flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
      <label htmlFor={htmlFor} className="text-xs font-medium text-brand">
        {children}
      </label>
      {mergeToken && (
        <code
          className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[10px] text-brand-muted"
          title="Insert this token in Cyber policy / template text"
        >
          {mergeToken}
        </code>
      )}
    </div>
  )
}

export function ManageCustomers() {
  const { isAdmin } = useAuth()
  const [customers, setCustomers] = useState([])
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [editId, setEditId] = useState(null)
  const [edit, setEdit] = useState(null)

  useEffect(() => {
    if (!isAdmin) return undefined
    let cancelled = false
    async function load() {
      setLoading(true)
      setError('')
      try {
        const [data, tmpl] = await Promise.all([
          customersApi.listCustomers(),
          templatesApi.listTemplates().catch(() => []),
        ])
        if (!cancelled) {
          setCustomers(data)
          setTemplates(tmpl)
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            typeof err.response?.data?.detail === 'string'
              ? err.response.data.detail
              : 'Could not load customers.',
          )
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [isAdmin])

  if (!isAdmin) {
    return <Navigate to="/" replace />
  }

  function fail(err, fallback) {
    const detail = err.response?.data?.detail
    if (typeof detail === 'string') setError(detail)
    else if (Array.isArray(detail)) {
      setError(detail.map((d) => d.msg || JSON.stringify(d)).join('; '))
    } else setError(fallback)
  }

  async function handleCreate(e) {
    e.preventDefault()
    if (busy || !form.name.trim()) return
    setBusy(true)
    setError('')
    try {
      const created = await customersApi.createCustomer({
        name: form.name.trim(),
        notes: form.notes.trim() || null,
        company_owner_representative:
          form.company_owner_representative.trim() || null,
        it_manager_system_administrator:
          form.it_manager_system_administrator.trim() || null,
        template: form.template,
      })
      setCustomers((prev) =>
        [...prev, created].sort((a, b) => a.name.localeCompare(b.name)),
      )
      setForm(emptyForm())
    } catch (err) {
      fail(err, 'Could not create customer.')
    } finally {
      setBusy(false)
    }
  }

  function startEdit(c) {
    setEditId(c.id)
    setEdit({
      name: c.name,
      notes: c.notes || '',
      company_owner_representative: c.company_owner_representative || '',
      it_manager_system_administrator: c.it_manager_system_administrator || '',
      is_active: c.is_active,
      dash_atera_id: c.dash_atera_id != null ? String(c.dash_atera_id) : '',
    })
  }

  async function handleSaveEdit(e) {
    e.preventDefault()
    if (!editId || busy) return
    setBusy(true)
    setError('')
    try {
      const body = {
        name: edit.name.trim(),
        notes: edit.notes.trim() || null,
        company_owner_representative:
          edit.company_owner_representative.trim() || null,
        it_manager_system_administrator:
          edit.it_manager_system_administrator.trim() || null,
        is_active: edit.is_active,
        dash_atera_id: edit.dash_atera_id.trim()
          ? Number(edit.dash_atera_id)
          : null,
      }
      const updated = await customersApi.updateCustomer(editId, body)
      setCustomers((prev) =>
        prev
          .map((c) => (c.id === editId ? updated : c))
          .sort((a, b) => a.name.localeCompare(b.name)),
      )
      setEditId(null)
      setEdit(null)
    } catch (err) {
      fail(err, 'Could not update customer.')
    } finally {
      setBusy(false)
    }
  }

  async function handleDelete(c) {
    if (busy) return
    if (
      !window.confirm(
        `Delete "${c.name}" and all its knowledge-base content? This cannot be undone.`,
      )
    ) {
      return
    }
    setBusy(true)
    setError('')
    try {
      await customersApi.deleteCustomer(c.id)
      setCustomers((prev) => prev.filter((row) => row.id !== c.id))
      if (editId === c.id) {
        setEditId(null)
        setEdit(null)
      }
    } catch (err) {
      fail(err, 'Could not delete customer.')
    } finally {
      setBusy(false)
    }
  }

  const logoInputRef = useRef(null)
  const [logoTargetId, setLogoTargetId] = useState(null)

  function pickLogo(customerId) {
    setLogoTargetId(customerId)
    window.setTimeout(() => logoInputRef.current?.click(), 0)
  }

  async function handleLogoSelected(e) {
    const file = e.target.files?.[0]
    const id = logoTargetId
    e.target.value = ''
    setLogoTargetId(null)
    if (!file || !id || busy) return
    setBusy(true)
    setError('')
    try {
      const updated = await customersApi.uploadCustomerLogo(id, file)
      setCustomers((prev) =>
        prev.map((row) => (row.id === id ? updated : row)),
      )
    } catch (err) {
      fail(err, 'Could not upload logo.')
    } finally {
      setBusy(false)
    }
  }

  async function handleRemoveLogo(customerId) {
    if (busy) return
    setBusy(true)
    setError('')
    try {
      const updated = await customersApi.deleteCustomerLogo(customerId)
      setCustomers((prev) =>
        prev.map((row) => (row.id === customerId ? updated : row)),
      )
    } catch (err) {
      fail(err, 'Could not remove logo.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <SettingsShell title="Customers">
      <div>
          <h1 className="font-display text-4xl tracking-tight">Manage customers</h1>
          <p className="mt-2 text-sm text-brand-muted">
            Add, rename, deactivate or delete customer organisations. Upload a
            company logo for the home-page cards. Deleting removes all
            categories and content for that customer.
          </p>

          <input
            ref={logoInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp,.png,.jpg,.jpeg,.gif,.webp"
            className="hidden"
            onChange={handleLogoSelected}
          />

          {error && (
            <p className="mt-4 text-sm text-red-700" role="alert">
              {error}
            </p>
          )}

          <form
            onSubmit={handleCreate}
            className="mt-8 space-y-3 rounded-lg border border-slate-200 bg-white/90 p-5 shadow-sm"
          >
            <p className="text-sm font-semibold text-brand">Add customer</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-sm sm:col-span-1">
                <span className="flex flex-wrap items-baseline justify-between gap-2 text-brand-muted">
                  <span>Name</span>
                  <code className="rounded bg-slate-100 px-1 font-mono text-[10px]">
                    {'{{customer_name}}'}
                  </code>
                </span>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                  placeholder="Company name"
                />
              </label>
              <label className="block text-sm">
                <span className="text-brand-muted">Template</span>
                <select
                  value={form.template}
                  onChange={(e) => setForm({ ...form, template: e.target.value })}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                >
                  {templates.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.name}
                      {t.categories?.length
                        ? ` (${t.categories.length} categories)`
                        : ''}
                    </option>
                  ))}
                  <option value="blank">Blank (no categories)</option>
                </select>
              </label>
              <label className="block text-sm sm:col-span-2">
                <span className="flex flex-wrap items-baseline justify-between gap-2 text-brand-muted">
                  <span>Company owner / representative</span>
                  <code className="rounded bg-slate-100 px-1 font-mono text-[10px]">
                    {'{{company_owner_representative}}'}
                  </code>
                </span>
                <input
                  value={form.company_owner_representative}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      company_owner_representative: e.target.value,
                    })
                  }
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                  placeholder="Name used on Cyber policy sign-off"
                />
              </label>
              <label className="block text-sm sm:col-span-2">
                <span className="flex flex-wrap items-baseline justify-between gap-2 text-brand-muted">
                  <span>IT manager / system administrator</span>
                  <code className="rounded bg-slate-100 px-1 font-mono text-[10px]">
                    {'{{it_manager_system_administrator}}'}
                  </code>
                </span>
                <input
                  value={form.it_manager_system_administrator}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      it_manager_system_administrator: e.target.value,
                    })
                  }
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                  placeholder="Optional — for future policy merge fields"
                />
              </label>
              <label className="block text-sm sm:col-span-2">
                <span className="text-brand-muted">Notes (optional)</span>
                <input
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                />
              </label>
            </div>
            <button
              type="submit"
              disabled={busy || !form.name.trim()}
              className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
            >
              Add customer
            </button>
          </form>

          {loading ? (
            <p className="mt-8 text-brand-muted">Loading…</p>
          ) : (
            <div className="mt-8 overflow-x-auto rounded-lg border border-slate-200 bg-white/90 shadow-sm">
              <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
                <thead className="bg-slate-50 text-brand-muted">
                  <tr>
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Dash ID</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {customers.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-4 py-6 text-brand-muted">
                        No customers yet.
                      </td>
                    </tr>
                  )}
                  {customers.map((c) => (
                    <tr key={c.id} className={!c.is_active ? 'opacity-60' : undefined}>
                      <td className="px-4 py-3 align-top">
                        {editId === c.id ? (
                          <form
                            className="min-w-[18rem] max-w-md space-y-3"
                            onSubmit={handleSaveEdit}
                          >
                            <div>
                              <FieldLabel htmlFor={`edit-name-${c.id}`} mergeToken="{{customer_name}}">
                                Customer name
                              </FieldLabel>
                              <input
                                id={`edit-name-${c.id}`}
                                value={edit.name}
                                onChange={(e) =>
                                  setEdit({ ...edit, name: e.target.value })
                                }
                                className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                                required
                              />
                            </div>
                            <div>
                              <FieldLabel htmlFor={`edit-notes-${c.id}`}>Notes</FieldLabel>
                              <input
                                id={`edit-notes-${c.id}`}
                                value={edit.notes}
                                onChange={(e) =>
                                  setEdit({ ...edit, notes: e.target.value })
                                }
                                className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                              />
                            </div>
                            <div>
                              <FieldLabel
                                htmlFor={`edit-owner-${c.id}`}
                                mergeToken="{{company_owner_representative}}"
                              >
                                Company owner / representative
                              </FieldLabel>
                              <input
                                id={`edit-owner-${c.id}`}
                                value={edit.company_owner_representative}
                                onChange={(e) =>
                                  setEdit({
                                    ...edit,
                                    company_owner_representative: e.target.value,
                                  })
                                }
                                className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                              />
                              <p className="mt-0.5 text-[10px] text-brand-muted">
                                Alias: <code>{'{{company_owner}}'}</code>
                              </p>
                            </div>
                            <div>
                              <FieldLabel
                                htmlFor={`edit-it-${c.id}`}
                                mergeToken="{{it_manager_system_administrator}}"
                              >
                                IT manager / system administrator
                              </FieldLabel>
                              <input
                                id={`edit-it-${c.id}`}
                                value={edit.it_manager_system_administrator}
                                onChange={(e) =>
                                  setEdit({
                                    ...edit,
                                    it_manager_system_administrator: e.target.value,
                                  })
                                }
                                className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                              />
                              <p className="mt-0.5 text-[10px] text-brand-muted">
                                Alias: <code>{'{{it_manager}}'}</code>
                              </p>
                            </div>
                            <div>
                              <FieldLabel htmlFor={`edit-dash-${c.id}`}>
                                Dash Atera ID
                              </FieldLabel>
                              <input
                                id={`edit-dash-${c.id}`}
                                value={edit.dash_atera_id}
                                onChange={(e) =>
                                  setEdit({
                                    ...edit,
                                    dash_atera_id: e.target.value,
                                  })
                                }
                                className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                              />
                            </div>
                            <label className="flex items-center gap-2 text-xs">
                              <input
                                type="checkbox"
                                checked={edit.is_active}
                                onChange={(e) =>
                                  setEdit({
                                    ...edit,
                                    is_active: e.target.checked,
                                  })
                                }
                              />
                              Active
                            </label>
                            <div className="flex gap-2">
                              <button
                                type="submit"
                                disabled={busy}
                                className="rounded bg-brand px-2 py-1 text-xs text-white hover:bg-slate-800"
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                className="text-xs text-brand-muted"
                                onClick={() => {
                                  setEditId(null)
                                  setEdit(null)
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </form>
                        ) : (
                          <div className="flex items-start gap-3">
                            <CustomerLogo
                              customerId={c.id}
                              logoFilename={c.logo_filename}
                              updatedAt={c.updated_at}
                              className="h-12 w-12"
                            />
                            <div className="min-w-0">
                              <p className="font-medium text-brand">{c.name}</p>
                              {c.notes && (
                                <p className="mt-0.5 text-xs text-brand-muted">
                                  {c.notes}
                                </p>
                              )}
                              {c.company_owner_representative && (
                                <p className="mt-0.5 text-xs text-brand-muted">
                                  Sign-off: {c.company_owner_representative}
                                </p>
                              )}
                              <Link
                                to={`/customers/${c.id}`}
                                className="mt-1 inline-block text-xs text-brand underline"
                              >
                                Open knowledge base
                              </Link>
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-brand-muted">
                        {c.dash_atera_id ?? '—'}
                      </td>
                      <td className="px-4 py-3 align-top text-xs">
                        {c.is_active ? 'Active' : 'Inactive'}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {editId !== c.id && (
                          <div className="flex flex-col gap-1">
                            <button
                              type="button"
                              className="text-left text-xs font-medium text-brand underline"
                              onClick={() => startEdit(c)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              className="text-left text-xs font-medium text-brand underline disabled:opacity-40"
                              onClick={() => pickLogo(c.id)}
                            >
                              {c.logo_filename ? 'Change logo' : 'Upload logo'}
                            </button>
                            {c.logo_filename ? (
                              <button
                                type="button"
                                disabled={busy}
                                className="text-left text-xs text-brand-muted underline disabled:opacity-40"
                                onClick={() => handleRemoveLogo(c.id)}
                              >
                                Remove logo
                              </button>
                            ) : null}
                            <button
                              type="button"
                              disabled={busy}
                              className="text-left text-xs text-red-700 underline disabled:opacity-40"
                              onClick={() => handleDelete(c)}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </SettingsShell>
  )
}
